import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Stack,
  Box,
  alpha,
  useTheme,
} from "@mui/material";
import { useResponsive } from "../../../core/hooks/useResponsive";

const getStatusColor = (status) => {
  switch (status) {
    case "SUCCESS":
      return "success";
    case "ERROR":
      return "error";
    case "PENDING":
      return "warning";
    default:
      return "default";
  }
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";
  try {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return String(timestamp);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  } catch (e) {
    return String(timestamp);
  }
};

/** Same four fields as the table row, stacked for narrow screens. */
const LogEntryCard = ({ log }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 1,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.5 }}>
        <Chip label={log.status || "—"} color={getStatusColor(log.status)} size="small" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }} />
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
          {log.stage || "—"}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: "0.62rem", color: "text.secondary" }}>{formatTimestamp(log.timestamp)}</Typography>
      {log.message && (
        <Typography sx={{ fontSize: "0.76rem", mt: 0.4, wordBreak: "break-word" }}>{log.message}</Typography>
      )}
    </Box>
  );
};

const OrderLogDialog = ({ open, onClose, orderLog, symbol }) => {
  const { isPhone, isCompact } = useResponsive();
  const rows = Array.isArray(orderLog) ? orderLog : [];
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isPhone}
      PaperProps={{ sx: { borderRadius: isPhone ? 0 : 2 } }}
    >
      <DialogTitle sx={{ px: { xs: 1.75, sm: 3 }, py: { xs: 1.25, sm: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant={isPhone ? "subtitle1" : "h6"} fontWeight={800} noWrap>
            Order Log - {symbol || "N/A"}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 1.25, sm: 3 } }}>
        {rows.length === 0 && isCompact ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            No order log available
          </Typography>
        ) : isCompact ? (
          // Four columns (timestamp, stage, status, message) need ~500px; stack them.
          <Stack spacing={1} sx={{ py: 0.5 }}>
            {rows.map((log, index) => (
              <LogEntryCard key={index} log={log} />
            ))}
          </Stack>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Stage</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length > 0 ? (
                  rows.map((log, index) => (
                    <TableRow key={index}>
                      <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                      <TableCell>{log.stage || "—"}</TableCell>
                      <TableCell>
                        <Chip label={log.status || "—"} color={getStatusColor(log.status)} size="small" />
                      </TableCell>
                      <TableCell>{log.message || ""}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No order log available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 1.75, sm: 3 }, pb: { xs: 1.5, sm: 2 } }}>
        <Button onClick={onClose} variant="contained" color="primary" fullWidth={isPhone}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderLogDialog;
